import type { Entity, EntityUsage } from "@esi/shared";
import { useQuery } from "@tanstack/react-query";
import { ApiError, ok } from "../api-errors.ts";
import { t } from "../i18n/i18n.ts";
import { useAdminApi } from "./admin-api.tsx";

export const adminKeys = {
  entities: ["admin", "entities"] as const,
  users: ["admin", "users"] as const,
  catalogue: (entityId: string | null) => ["admin", "settings", "catalogue", entityId] as const,
};

/** The entities the caller administers (the whole tree for tenant admins). */
export function useAdminEntities() {
  const { admin } = useAdminApi();
  return useQuery({
    queryKey: adminKeys.entities,
    queryFn: async (): Promise<Entity[]> => ok(await admin.entities.$get()),
    retry: false,
  });
}

export interface TreeRow {
  entity: Entity;
  /** 0 for the top of the caller's scope. */
  depth: number;
  /**
   * A top row is the root or an entity the caller administers from its own membership: only
   * what is below it can be renamed, moved or deleted (the root can be renamed by tenant admins).
   */
  top: boolean;
}

/** Depth-first order, children by name, with each row's depth inside the caller's scope. */
export function treeRows(entities: readonly Entity[]): TreeRow[] {
  const ids = new Set(entities.map((e) => e.id));
  const children = new Map<string | null, Entity[]>();
  for (const e of entities) {
    const parent = e.parentId && ids.has(e.parentId) ? e.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), e]);
  }
  const rows: TreeRow[] = [];
  const visit = (parent: string | null, depth: number) => {
    const sorted = [...(children.get(parent) ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    for (const entity of sorted) {
      rows.push({ entity, depth, top: depth === 0 });
      visit(entity.id, depth + 1);
    }
  };
  visit(null, 0);
  return rows;
}

/** Whether `candidate` is `entity` or one of its descendants. */
export const inSubtree = (candidate: Entity, entity: Entity) => candidate.path.includes(entity.id);

/** A refused backoffice request in plain words, by the server's `code`, else its message. */
export function adminRefusal(error: Error): string {
  if (!(error instanceof ApiError)) {
    return error.message;
  }
  switch (error.code) {
    case "self_disable":
      return t("users.refused.self");
    case "last_tenant_admin":
      return t("users.refused.lastAdmin");
    case "entity_not_empty":
      return t("entities.refused.notEmpty");
  }
  if (error.status === 403) {
    // A 403 has several causes (scope, a lock set above, a role only tenant admins grant).
    return t("errors.forbidden");
  }
  if (error.status === 422 && error.code === "unprocessable") {
    return error.explained ? error.message : t("errors.refused");
  }
  return error.message;
}

/** The non-zero counts of a refused delete, in words. */
export function usageLines(usage: Partial<EntityUsage> | undefined): string[] {
  if (!usage) {
    return [];
  }
  return (["children", "members", "projects", "groups", "orgCodes", "promotions"] as const)
    .filter((k) => (usage[k] ?? 0) > 0)
    .map((k) => t(`entities.usage.${k}`, { count: usage[k] ?? 0 }));
}
